import { parseRemotePayload, SeatAssignmentModule } from './SeatAssignmentModule';
import { RemoteSeatPayload } from './types';

/**
 * The realtime bridge (ESAPA-104 / mapd-issues#656).
 *
 * This is the *single inbound entry point*. The several inconsistent handlers
 * that used to listen for `tableStatus` / `attendeeProfile` events and mutate
 * the canvas directly are replaced by one subscription that feeds every inbound
 * realtime payload into the module's `applyRemote`. Because `applyRemote` is a
 * Remote Change, applying an inbound event can never write to the backend
 * (ADR-0004), so an echo loop between two sessions is impossible by construction.
 *
 * Connection state drives the UI directly: a drop marks the module
 * disconnected, and a restore refetches and only then marks it connected, so a
 * change missed while the socket was down is not lost and the admin is never
 * told the room is live while it is still stale. There is deliberately no
 * background poll (ADR-0004); connection state does that job instead.
 */

/** The minimal surface the bridge listens on — usually `window`. */
export interface RealtimeSource {
  addEventListener(type: string, listener: (event: Event) => void): void;
  removeEventListener(type: string, listener: (event: Event) => void): void;
}

export interface RealtimeBridgeOptions {
  module: SeatAssignmentModule;
  /** Source of the realtime CustomEvents. Defaults to `window`. */
  source?: RealtimeSource;
  /**
   * Only apply inbound events for this event id. An event carrying a different
   * `payload.Event__c` is ignored; an event with no event id is always applied.
   */
  eventId?: string;
  /** Seat-bearing event types to subscribe. Defaults to the two channels. */
  seatEventTypes?: string[];
  /** Event fired when the realtime socket connects. Defaults to `realtimeConnected`. */
  connectedEventType?: string;
  /** Event fired when the realtime socket disconnects. Defaults to `realtimeDisconnected`. */
  disconnectedEventType?: string;
  /**
   * Whether the socket is already up at the moment the bridge attaches.
   *
   * Gala Seating's panel mounts long after the application opened its socket,
   * so `connect` has usually already fired and will not fire again. Without
   * this seed the module would sit on `connecting` for the whole session and
   * report a healthy channel as dead (mapd-issues#664). Consulted exactly once,
   * at attach; the module stays the one source of truth afterwards.
   */
  isConnected?: () => boolean;
}

const DEFAULT_SEAT_EVENT_TYPES = ['tableStatus', 'attendeeProfile'];

/**
 * Subscribe the module to the realtime channels and connection signal. Returns
 * a disposer that removes every listener.
 */
export function attachRealtimeBridge(options: RealtimeBridgeOptions): () => void {
  const { module, eventId } = options;
  const source =
    options.source ?? (typeof window !== 'undefined' ? (window as unknown as RealtimeSource) : undefined);
  if (!source) return () => undefined;

  const seatEventTypes = options.seatEventTypes ?? DEFAULT_SEAT_EVENT_TYPES;
  const connectedEventType = options.connectedEventType ?? 'realtimeConnected';
  const disconnectedEventType = options.disconnectedEventType ?? 'realtimeDisconnected';

  // Set by a drop, and cleared only once the refetch that recovers the changes
  // missed while down has landed — a refetch that failed leaves the recovery
  // still owed, so the next connect does it again rather than declaring the
  // room live.
  let recoveryOwed = false;
  let recovering = false;

  const onSeatEvent = (event: Event) => {
    const detail = (event as CustomEvent).detail as RemoteSeatPayload | undefined;
    if (!detail) return;
    if (eventId && !matchesEvent(detail, eventId)) return;

    // A cancelled Ticket carries no seat field, so what it means for seating is
    // the bridge's call to make: nobody is left to sit anywhere.
    if (isCancelled(detail)) {
      const parsed = parseRemotePayload(detail);
      if (parsed) module.applyRemoteUnseat(parsed.ticketId);
      return;
    }

    module.applyRemote(detail);
  };

  const onDisconnected = () => {
    recoveryOwed = true;
    module.setConnectionState('disconnected');
  };

  const onConnected = () => {
    // Refetch only on a genuine restore, so the first connect after load does
    // not trigger a redundant fetch. A change missed while down is recovered.
    if (!recoveryOwed) {
      module.setConnectionState('connected');
      return;
    }
    // A second connect while the recovering refetch is in flight is the same
    // recovery, not another one.
    if (recovering) return;
    recovering = true;
    // The socket coming back does not make the room current — the events
    // published while it was down are gone (ADR-0004). So the connection stays
    // down until the refetch that recovers them lands, and a refetch that
    // fails leaves it down, because the room really may still be stale.
    void module.onReconnect().then(
      () => {
        recovering = false;
        recoveryOwed = false;
        module.setConnectionState('connected');
      },
      () => {
        recovering = false;
      },
    );
  };

  seatEventTypes.forEach((type) => source.addEventListener(type, onSeatEvent));
  source.addEventListener(connectedEventType, onConnected);
  source.addEventListener(disconnectedEventType, onDisconnected);

  if (options.isConnected?.()) module.setConnectionState('connected');

  return () => {
    seatEventTypes.forEach((type) => source.removeEventListener(type, onSeatEvent));
    source.removeEventListener(connectedEventType, onConnected);
    source.removeEventListener(disconnectedEventType, onDisconnected);
  };
}

/** Ticket states that mean the booking is gone, however it was spelled. */
const CANCELLED_STATES = ['cancelled', 'deleted'];

/** Whether an inbound payload says the Ticket's booking no longer exists. */
function isCancelled(detail: RemoteSeatPayload): boolean {
  const payload = (detail.payload ?? detail) as Record<string, unknown>;
  const status = payload?.ticket_status__c ?? payload?.Ticket_Status__c;
  return typeof status === 'string' && CANCELLED_STATES.includes(status.toLowerCase());
}

/**
 * Whether an inbound payload belongs to the given event. An event id embedded
 * in the payload that does not match is filtered out; a payload without one is
 * left to the module (its Ticket lookup is the real guard).
 */
function matchesEvent(detail: RemoteSeatPayload, eventId: string): boolean {
  const payload = detail.payload as Record<string, unknown> | undefined;
  const carried = payload?.Event__c;
  if (typeof carried === 'string' && carried.length > 0) {
    return carried === eventId;
  }
  return true;
}
